import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { signOut } from "firebase/auth";
import {
  collection,
  getDocs,
  query,
  where,
} from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { auth, db, functions } from "../../../firebase";
import { GroupData } from "../../../types/GroupData";

interface SongFieldSet {
  songName1: string;
  songLyrics1: string;
  songComposed1: string;
  songArranged1: string;
  songName2: string;
  songLyrics2: string;
  songComposed2: string;
  songArranged2: string;
  songName3: string;
  songLyrics3: string;
  songComposed3: string;
  songArranged3: string;
  songName4: string;
  songLyrics4: string;
  songComposed4: string;
  songArranged4: string;
}

const toSongFieldSet = (group: GroupData): SongFieldSet => ({
  songName1: group.songName1 ?? "",
  songLyrics1: group.songLyrics1 ?? "",
  songComposed1: group.songComposed1 ?? "",
  songArranged1: group.songArranged1 ?? "",
  songName2: group.songName2 ?? "",
  songLyrics2: group.songLyrics2 ?? "",
  songComposed2: group.songComposed2 ?? "",
  songArranged2: group.songArranged2 ?? "",
  songName3: group.songName3 ?? "",
  songLyrics3: group.songLyrics3 ?? "",
  songComposed3: group.songComposed3 ?? "",
  songArranged3: group.songArranged3 ?? "",
  songName4: group.songName4 ?? "",
  songLyrics4: group.songLyrics4 ?? "",
  songComposed4: group.songComposed4 ?? "",
  songArranged4: group.songArranged4 ?? "",
});

interface EntryCardProps {
  group: GroupData;
}

// entryClassはFirestore上ではQuartetContest等のキーで保存されているため、
// 同じメールアドレスで複数グループを管理している担当者が見分けやすいよう表示用のラベルに変換する
const ENTRY_CLASS_LABEL: Record<string, string> = {
  QuartetContest: "Quartet Contest",
  ChorusContest: "Chorus Contest",
  EvaluationOnly: "Evaluation Only",
};

const getEntryClassLabel = (entryClass: string) =>
  ENTRY_CLASS_LABEL[entryClass] ?? entryClass;

const updateGroupSongInfo = httpsCallable(functions, "updateGroupSongInfo");

const EntryCard: React.FC<EntryCardProps> = ({ group }) => {
  const [fields, setFields] = useState<SongFieldSet>(() =>
    toSongFieldSet(group),
  );
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [savedMessage, setSavedMessage] = useState("");

  const isQuartet = group.entryClass === "QuartetContest";
  const year = group.createdAt?.toDate().getFullYear() ?? "-";

  const handleChange = (key: keyof SongFieldSet, value: string) => {
    setFields((prev) => ({ ...prev, [key]: value }));
  };

  const handleSave = async () => {
    if (isSaving) return;

    setIsSaving(true);
    setErrorMessage("");
    setSavedMessage("");

    try {
      await updateGroupSongInfo({
        groupId: group.id,
        ...fields,
      });

      setSavedMessage("曲情報を更新しました。");
      setIsEditing(false);
    } catch (error) {
      console.error("曲情報の更新エラー:", error);
      const message =
        error instanceof Error ? error.message : "更新に失敗しました。";
      setErrorMessage(message);
    } finally {
      setIsSaving(false);
    }
  };

  const songNumbers = isQuartet ? [1, 2, 3, 4] : [1, 2];

  return (
    <section className="card-list">
      <article className="list-card">
        <div className="flex-space-between">
          <div className="flex-align-center">
            <p className="entry-number">{group.entryNumber}</p>
            <h2>
              {year}年 {group.groupName}
              <span className="sub-text">
                　／　{getEntryClassLabel(group.entryClass)}
              </span>
            </h2>
          </div>

          <div className="list-edit">
            <button
              className="editbtn"
              onClick={() => {
                if (isEditing) {
                  setFields(toSongFieldSet(group));
                }
                setIsEditing((prev) => !prev);
                setErrorMessage("");
                setSavedMessage("");
              }}
            >
              {isEditing ? "キャンセル" : "曲情報を編集"}
            </button>
          </div>
        </div>

        <div className="memo">
          <p>エントリークラス：{getEntryClassLabel(group.entryClass)}</p>
          <p>担当者：{group.picName}</p>
        </div>

        {songNumbers.map((num) => {
          const nameKey = `songName${num}` as keyof SongFieldSet;
          const lyricsKey = `songLyrics${num}` as keyof SongFieldSet;
          const composedKey = `songComposed${num}` as keyof SongFieldSet;
          const arrangedKey = `songArranged${num}` as keyof SongFieldSet;

          return (
            <div className="admin-edit entry-form" key={num}>
              <div className="form-contents section-start">
                <label className="form-label">
                  {isQuartet && (num <= 2 ? "SemiFinal " : "Final ")}
                  {num % 2 === 1 ? "1曲目" : "2曲目"}
                </label>
              </div>

              {isEditing ? (
                <>
                  <div className="form-contents second">
                    <label className="form-label">曲名</label>
                    <input
                      value={fields[nameKey]}
                      onChange={(e) => handleChange(nameKey, e.target.value)}
                    />
                  </div>
                  <div className="form-contents middle-size third">
                    <label className="form-label">作詞者</label>
                    <input
                      value={fields[lyricsKey]}
                      onChange={(e) => handleChange(lyricsKey, e.target.value)}
                    />
                  </div>
                  <div className="form-contents middle-size third">
                    <label className="form-label">作曲者</label>
                    <input
                      value={fields[composedKey]}
                      onChange={(e) =>
                        handleChange(composedKey, e.target.value)
                      }
                    />
                  </div>
                  <div className="form-contents middle-size third">
                    <label className="form-label">編曲者</label>
                    <input
                      value={fields[arrangedKey]}
                      onChange={(e) =>
                        handleChange(arrangedKey, e.target.value)
                      }
                    />
                  </div>
                </>
              ) : (
                <p>
                  {fields[nameKey] || "（未登録）"}
                  {fields[arrangedKey] && (
                    <span className="sub-text"> [{fields[arrangedKey]}]</span>
                  )}
                </p>
              )}
            </div>
          );
        })}

        {isEditing && (
          <div className="edit-footer">
            <button onClick={handleSave} disabled={isSaving}>
              {isSaving ? "保存中..." : "保存する"}
            </button>
          </div>
        )}

        {errorMessage && <p className="error-message">{errorMessage}</p>}
        {savedMessage && <p>{savedMessage}</p>}
      </article>
    </section>
  );
};

const Portal: React.FC = () => {
  const navigate = useNavigate();

  const [groups, setGroups] = useState<GroupData[]>([]);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    const fetchMyGroups = async () => {
      const email = auth.currentUser?.email;

      if (!email) {
        setLoading(false);
        return;
      }

      try {
        // groups全件ではなく、自分のメールアドレスに一致するものだけを取得する
        const groupsQuery = query(
          collection(db, "groups"),
          where("picMail", "==", email.trim().toLowerCase()),
        );

        const snapshot = await getDocs(groupsQuery);

        const fetchedGroups: GroupData[] = snapshot.docs
          .map((groupDoc) => ({
            ...(groupDoc.data() as Omit<GroupData, "id">),
            id: groupDoc.id,
          }))
          .filter((group) => group.isDeleted !== true)
          .sort((a, b) => {
            // 同じメールアドレスに複数グループが紐づく場合でも迷わないよう、
            // 年度→エントリークラス→グループ名の順で安定した並び順にする
            const aYear = a.createdAt?.toDate().getFullYear() ?? 0;
            const bYear = b.createdAt?.toDate().getFullYear() ?? 0;
            if (aYear !== bYear) return bYear - aYear;

            const aClassLabel = getEntryClassLabel(a.entryClass);
            const bClassLabel = getEntryClassLabel(b.entryClass);
            if (aClassLabel !== bClassLabel) {
              return aClassLabel.localeCompare(bClassLabel);
            }

            return a.groupName.localeCompare(b.groupName, "ja");
          });

        setGroups(fetchedGroups);
      } catch (error) {
        console.error("マイページ情報取得エラー:", error);
        setErrorMessage("情報の取得に失敗しました。");
      } finally {
        setLoading(false);
      }
    };

    fetchMyGroups();
  }, []);

  const handleLogout = async () => {
    await signOut(auth);
    navigate("/portal/login", { replace: true });
  };

  return (
    <div className="table-contents">
      <div className="table-main">
        <div className="table-header">
          <h1>マイページ</h1>

          <p className="logout" onClick={handleLogout}>
            ログアウト
          </p>
        </div>

        {loading && <p>読み込み中...</p>}

        {!loading && errorMessage && <p className="error-message">{errorMessage}</p>}

        {!loading && !errorMessage && groups.length === 0 && (
          <p>
            ログイン中のメールアドレスに一致するエントリーが見つかりませんでした。
          </p>
        )}

        {!loading && !errorMessage && groups.length > 0 && (
          <p className="portal-summary">
            ログイン中のメールアドレスに紐づくエントリーが{groups.length}
            件見つかりました。同じメールアドレスで複数グループを管理している場合も、
            グループ名・年度・エントリークラスで区別しながらそれぞれ個別に編集できます。
          </p>
        )}

        {groups.map((group) => (
          <EntryCard key={group.id} group={group} />
        ))}
      </div>
    </div>
  );
};

export default Portal;
